// src/services/api.js
import axios from "axios";
import { API_BASE_URL } from "../constants/api-urls";
import { ROUTES } from "../constants/routes";
import StorageService from "./StorageService";
import { applyBulkImportTimeout } from "../constants/bulkUpload";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
});

// Attach token to every request
api.interceptors.request.use((config) => {
  applyBulkImportTimeout(config);
  const token = StorageService.getToken(); // ✅ read token correctly
  
  // Debug logging (only when token is missing; set to true for verbose token logs)
  if (!token && process.env.NODE_ENV !== 'production') {
    console.warn('API Interceptor: No token found for request', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL
    });
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Don't set Content-Type for FormData - let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  return config;
});


api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const currentPath = window.location.pathname;
      const pagesToExclude = [
        '/seller-rfq-list',
        '/submit-quotation-to-buyer',
        '/generate-invoice',
        '/invoice-list',
        '/forgot-password',
        '/marketplace/products',
        '/hub-home',
        '/contract-manufacturers',
        '/contract-manufacturers/profile',
        '/contract-manufacturers/rfq',
        '/infrastructure-leasing/listing',
      ];
      
      const shouldExcludeRedirect = pagesToExclude.some(page => 
        currentPath === page || currentPath.startsWith(page)
      );
      
      if (shouldExcludeRedirect) {
        // Allow the page to handle 401 errors gracefully without clearing auth mid-flow
        console.warn(`401 error on ${currentPath} - allowing page to handle it`);
        return Promise.reject(err);
      }
      
      StorageService.clearAuth();
      
      // Redirect to login if not already on login page
      if (currentPath !== '/login' && currentPath !== '/') {
        setTimeout(() => {
          const newPath = window.location.pathname;
          const shouldStillExclude = pagesToExclude.some(page => 
            newPath === page || newPath.includes(page)
          );
          
          if (newPath !== '/login' && newPath !== '/' && !shouldStillExclude) {
            window.location.href = '/login';
          }
        }, 500);
      }
    }
    return Promise.reject(err);
  }
);


export default api;
